import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/utils';

const switchVariants = cva(
  'peer inline-flex shrink-0 items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bhanzu-orange focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-gray-200 data-[pressed=true]:bg-bhanzu-orange',
        outline:
          'border-gray-300 bg-transparent data-[pressed=true]:border-bhanzu-orange data-[pressed=true]:bg-bhanzu-orange',
        ghost:
          'bg-gray-100 data-[pressed=true]:bg-bhanzu-orange-light-cardbg data-[pressed=true]:border-bhanzu-orange',
        solid: 'bg-gray-300 data-[pressed=true]:bg-bhanzu-orange',
      },
      size: {
        default: 'h-6 w-11',
        sm: 'h-5 w-9',
        lg: 'h-7 w-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const thumbVariants = cva(
  'pointer-events-none block rounded-full bg-white shadow-lg ring-0 transition-transform',
  {
    variants: {
      size: {
        default: 'h-5 w-5 data-[pressed=true]:translate-x-5',
        sm: 'h-4 w-4 data-[pressed=true]:translate-x-4',
        lg: 'h-6 w-6 data-[pressed=true]:translate-x-5',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

export interface ToggleProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  pressed?: boolean;
  defaultPressed?: boolean;
  onChange?: (pressed: boolean) => void;
  variant?: 'default' | 'outline' | 'ghost' | 'solid';
  size?: 'default' | 'sm' | 'lg';
}

const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      className,
      variant,
      size,
      pressed: controlledPressed,
      defaultPressed = false,
      onChange,
      ...props
    },
    ref
  ) => {
    const [internalPressed, setInternalPressed] =
      React.useState(defaultPressed);

    const isControlled = controlledPressed !== undefined;
    const pressed = isControlled ? controlledPressed : internalPressed;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!isControlled) {
        setInternalPressed(prev => !prev);
      }
      onChange?.(!pressed);
      props?.onClick?.(event);
    };

    return (
      <button
        type="button"
        role="switch"
        className={cn(switchVariants({ variant, size, className }))}
        data-pressed={pressed}
        aria-checked={pressed}
        ref={ref}
        onClick={handleClick}
        {...props}
      >
        <span className={cn(thumbVariants({ size }))} data-pressed={pressed} />
      </button>
    );
  }
);

Toggle.displayName = 'Toggle';

export default Toggle;
export { switchVariants };
